<template>
  <Message
    class="shipment-service-incident-message rygen-message"
    :icon="`pi ${iconType}`"
    :severity="severity"
  >
    {{
      t('events.incidentMessage', {
        count: shipmentServiceIncidents.length,
        param: serviceIncidentTypeName,
      })
    }}

    <RygenButton
      v-if="highestPriorityIncident?.id"
      mode="link"
      class="whats-this-link cursor-pointer underline"
      :label="
        t('events.clickableMessage', {
          count: shipmentServiceIncidents.length,
        })
      "
      @click="emit('scroll-trigger', highestPriorityIncident.id)"
    />
  </Message>
</template>

<script lang="ts" setup>
import type { PropType, Ref } from 'vue';
import { computed, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { Optional, ServiceIncidentType } from '@rygen/rygen-ui/model';

import { ShipmentEventSeverity, ShipmentServiceIncident } from '@/model';

import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import Message, { MessageProps } from 'primevue/message';

const ORDERED_SEVERITIES = [
  ShipmentEventSeverity.CRITICAL,
  ShipmentEventSeverity.WARNING,
  ShipmentEventSeverity.NORMAL,
];

const ICONS_BY_SEVERITY: Record<ShipmentEventSeverity, string> = {
  [ShipmentEventSeverity.NORMAL]: 'pi-info-circle',
  [ShipmentEventSeverity.WARNING]: 'pi-exclamation-triangle',
  [ShipmentEventSeverity.CRITICAL]: 'pi-times-circle',
};

const MESSAGE_SEVERITY_BY_EVENT_SEVERITY: Record<
  ShipmentEventSeverity,
  MessageProps['severity']
> = {
  [ShipmentEventSeverity.NORMAL]: 'info',
  [ShipmentEventSeverity.WARNING]: 'warn',
  [ShipmentEventSeverity.CRITICAL]: 'error',
};

const props = defineProps({
  serviceIncidentTypes: {
    type: Array as PropType<ServiceIncidentType[]>,
    required: true,
  },
  shipmentServiceIncidents: {
    type: Array as PropType<ShipmentServiceIncident[]>,
    default: () => [],
  },
});
const { serviceIncidentTypes, shipmentServiceIncidents } = toRefs(props);
const emit = defineEmits<{
  (event: 'scroll-trigger', serviceIncidentId: number): void;
}>();

const { t } = useI18n();

const incidentsSortedBySeverity = computed(() => {
  return shipmentServiceIncidents.value.toSorted((a, b) => {
    const aSeverity = a.severity ?? ShipmentEventSeverity.NORMAL;
    const bSeverity = b.severity ?? ShipmentEventSeverity.NORMAL;

    const aIndex = ORDERED_SEVERITIES.indexOf(aSeverity);
    const bIndex = ORDERED_SEVERITIES.indexOf(bSeverity);

    return aIndex - bIndex;
  });
});

const highestPriorityIncident = computed(
  (): Optional<ShipmentServiceIncident> => {
    return incidentsSortedBySeverity.value[0];
  },
);

const serviceIncidentTypeName = computed(() => {
  const serviceIncidentType = serviceIncidentTypes.value.find(
    (type) => type.naturalId === highestPriorityIncident.value?.incidentType,
  );

  return (
    serviceIncidentType?.name ??
    highestPriorityIncident.value?.incidentType ??
    ''
  );
});

const iconType: Ref<string> = computed(() => {
  const severity =
    highestPriorityIncident.value?.severity ?? ShipmentEventSeverity.NORMAL;
  return ICONS_BY_SEVERITY[severity];
});

const severity = computed((): MessageProps['severity'] => {
  const severity =
    highestPriorityIncident.value?.severity ?? ShipmentEventSeverity.NORMAL;
  return MESSAGE_SEVERITY_BY_EVENT_SEVERITY[severity];
});
</script>

<style>
.p-message.shipment-service-incident-message {
  .p-message-text {
    display: flex;
    gap: var(--sp-2);
    align-items: center;
  }
}
</style>
